import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useSelector } from 'react-redux';

import useSearch from '../../hooks/useSearch';
import { useSaveSettings } from '../../hooks/useSaveSettings';
import { CATEGORIES, SUBCATEGORIES } from '../../common/categories';
import { WEBHOOK_FIELDS, EMAIL_FIELDS, SYSLOG_FIELDS_V2 } from '../../common/fields';
import { fieldsReducer, initFieldsState, mapToSettings } from './AlertsManagementReducer';
import { httpGet, httpDelete, isUserPermitted } from '../../../utils';
import { ROLES } from '@silverfort-nodelibs/common/utils/consts';
import NewCategory from '../../components/Category/NewCategory';
import { Icons, Icon, Table } from '@silverfort/sf-components';
import AlertsManagementFilters from './AlertsManagementFilters';
import AlertsManagementGallery from './AlertsManagementGallery';
import _ from 'lodash';
import * as actions from '../../containers/AlertsManagement/AlertsManagementActions';
import AlertsManagementSubTable from './AlertsManagementSubTable';
import CustomIntegrationDetails from '../../components/CustomIntegration/CustomIntegrationDetails';
import WebhookDetails from '../../components/Webhook/WebhookDetails';
import SyslogServerDetails from '../../components/SyslogServers/SyslogServersDetails';
import EmailDetails from '../../components/Email/EmailDetails';
import { useHistory, useLocation } from 'react-router-dom';
import { useTabs } from '../../hooks/useTabs';
import { useVisibleTabs } from '../../hooks/lockedFeatures/useVisibleTabs';

const { ALERTS_MANAGEMENT } = CATEGORIES;
const { WEBHOOK, SYSLOG_SERVER, EMAIL, CUSTOM_INTEGRATIONS } = SUBCATEGORIES;

const ALERTS_MANAGEMENT_HASH_URLS = {
    grid: { id: 'grid', name: 'Notification Integrations' },
    addIntegration: { id: 'add', name: 'Add Integration' }
};

const IntegrationEditorWrapper = ({ children }) => (
    <div className="bg-sf-white h-[calc(100vh-62px)] py-5 px-6 overflow-y-auto">
        <div className="font-medium text-sf-gray-800 h-16">Edit Integration</div>
        {children}
    </div>
);

const AlertsManagement = ({ saveFunc, systemMode }) => {
    const history = useHistory();
    const location = useLocation();

    const settings = useSelector((state) => state.settings.settings);
    const [isGalleryOpen, setIsGalleryOpen] = useState(false);

    const [fields, dispatch] = useReducer(fieldsReducer, { settings }, initFieldsState);
    const visibleFields = useSearch(useVisibleTabs(fields));
    const [selectedTab, setSelectedTab] = useTabs(ALERTS_MANAGEMENT, ALERTS_MANAGEMENT_HASH_URLS);
    const [save] = useSaveSettings(saveFunc, settings, mapToSettings, fields, '', dispatch);

    const user = useSelector((state) => state.userData.user);
    const isEditor = isUserPermitted(user, 'settings', ROLES.EDITOR.value);

    const [customIntegrationsSchema, setCustomIntegrationsSchema] = useState({});

    const [allEmails, setAllEmails] = useState({});
    const [allWebhooks, setAllWebhooks] = useState({});
    const [allSyslogServers, setAllSyslogServers] = useState({});
    const [allCustomIntegrations, setAllCustomIntegrations] = useState({});

    const [sortBy, setSortBy] = useState({ columnKey: 'providerName', direction: 'asc' });
    const [selectedFilters, setSelectedFilters] = useState({ providerType: [] });
    const [searchText, setSearchText] = useState('');

    const [emailIdToEdit, setEmailIdToEdit] = useState('');
    const [webhookIdToEdit, setWebhookIdToEdit] = useState('');
    const [syslogServerIdToEdit, setSyslogServerIdToEdit] = useState('');
    const [customIntegrationIdentifiersToEdit, setCustomIntegrationIdentifiersToEdit] = useState();

    const [categoryIdToSave, setCategoryIdToSave] = useState();

    const hasHttpProxy = settings.HTTPProxy || settings.HTTPSProxy;

    useEffect(() => {
        const getIntegrationsEventSchema = async () => {
            const result = await httpGet(`/settings/integrationsEventSchema`);
            if (result?.data?.integrationsEventSchema) {
                setCustomIntegrationsSchema(result.data.integrationsEventSchema);
            }
        };
        getIntegrationsEventSchema();
    }, []);

    useEffect(() => {
        if (!categoryIdToSave) {
            return;
        }
        const categoryToFieldIdMap = {
            [EMAIL.ID]: EMAIL_FIELDS.EMAILS.ID,
            [WEBHOOK.ID]: WEBHOOK_FIELDS.WEBHOOKS.ID,
            [SYSLOG_SERVER.ID]: SYSLOG_FIELDS_V2.SYSLOG_SERVERS.ID,
            [CUSTOM_INTEGRATIONS.ID]: CUSTOM_INTEGRATIONS.ID
        };

        const fieldId = categoryToFieldIdMap[categoryIdToSave];
        const field = visibleFields?.[categoryIdToSave]?.fields?.[fieldId];

        if (field?.isDirty) {
            save(false, categoryIdToSave);
            setCategoryIdToSave(undefined);
        }
    }, [visibleFields, categoryIdToSave, save]);

    useEffect(() => {
        if (!Object.keys(customIntegrationsSchema).length) {
            return;
        }
        const customIntegrations = visibleFields[CUSTOM_INTEGRATIONS.ID]?.fields[CUSTOM_INTEGRATIONS.ID].value;
        setAllCustomIntegrations(customIntegrations && customIntegrations !== '{}' ? customIntegrations : {});
    }, [customIntegrationsSchema, visibleFields]);

    useEffect(() => {
        setAllEmails(visibleFields[EMAIL.ID]?.fields[EMAIL_FIELDS.EMAILS.ID].value ?? {});
        setAllWebhooks(visibleFields[WEBHOOK.ID]?.fields[WEBHOOK_FIELDS.WEBHOOKS.ID].value ?? {});
        setAllSyslogServers(visibleFields[SYSLOG_SERVER.ID]?.fields[SYSLOG_FIELDS_V2.SYSLOG_SERVERS.ID].value ?? {});
    }, [visibleFields]);

    const saveCustomIntegrations = useCallback(
        (integrationKey, integrationValue) => {
            const json = _.cloneDeep(allCustomIntegrations);

            if (integrationValue.length) {
                json[integrationKey] = integrationValue;
            } else {
                delete json[integrationKey];
            }

            dispatch({
                type: actions.UPDATE_INTEGRATIONS_EVENT_CONF,
                payload: { value: json }
            });
            setCategoryIdToSave(CUSTOM_INTEGRATIONS.ID);
        },
        [allCustomIntegrations]
    );

    const hasValues = [
        allWebhooks,
        allSyslogServers,
        allEmails,
        Object.keys(allCustomIntegrations)?.filter(
            (integrationId) => customIntegrationsSchema?.properties[integrationId]
        )
    ].some((value) => Object.keys(value).length);

    const handleAddIntegration = () => {
        history.push(`${location.pathname}#${ALERTS_MANAGEMENT_HASH_URLS.addIntegration.id}`);
    };

    const handleCloseGallery = () => {
        history.push(`${location.pathname}#${ALERTS_MANAGEMENT_HASH_URLS.grid.id}`);
    };

    useEffect(() => {
        if (location.hash === `#${ALERTS_MANAGEMENT_HASH_URLS.addIntegration.id}`) {
            setIsGalleryOpen(true);
        } else if (location.hash === `#${ALERTS_MANAGEMENT_HASH_URLS.grid.id}`) {
            setIsGalleryOpen(false);
        }
    }, [location.hash]);

    const buildRows = useCallback(() => {
        const filteredEmails =
            !searchText || EMAIL.NAME.toLowerCase().includes(searchText)
                ? allEmails
                : _.pickBy(allEmails, (email) => email.name.toLowerCase().includes(searchText));

        const filteredSyslogServers =
            !searchText || SYSLOG_SERVER.NAME.toLowerCase().includes(searchText)
                ? allSyslogServers
                : _.pickBy(allSyslogServers, (syslogServer) => syslogServer.name.toLowerCase().includes(searchText));

        const filteredWebhooks =
            !searchText || WEBHOOK.NAME.toLowerCase().includes(searchText)
                ? allWebhooks
                : _.pickBy(allWebhooks, (webhook) => webhook.name.toLowerCase().includes(searchText));

        const filteredCustomIntegrations = Object.entries(allCustomIntegrations)
            .filter(([providerKey]) => customIntegrationsSchema.properties[providerKey])
            .reduce((acc, [providerKey, integrations]) => {
                const providerSchema = customIntegrationsSchema.properties[providerKey];

                const filteredIntegrations =
                    !searchText || providerSchema.name.toLowerCase().includes(searchText)
                        ? integrations
                        : integrations.filter((integration) => integration.name.toLowerCase().includes(searchText));
                if (filteredIntegrations.length) {
                    acc[providerKey] = filteredIntegrations;
                }
                return acc;
            }, {});

        const rows = [];
        if (Object.keys(filteredWebhooks).length) {
            rows.push({
                providerName: WEBHOOK.NAME,
                providerType: 'notifications',
                icon: Icons.Webhook,
                totalInstances: Object.keys(allWebhooks).length,
                extendedData: (
                    <AlertsManagementSubTable
                        isEditor={isEditor}
                        providerName={WEBHOOK.NAME}
                        integrations={Object.values(filteredWebhooks)}
                        handleEditIntegration={(integrationIdToEdit) => {
                            setWebhookIdToEdit(integrationIdToEdit);
                        }}
                        handleDeleteIntegration={(integrationIdToRemove) => {
                            dispatch({ type: actions.REMOVE_WEBHOOK, payload: integrationIdToRemove });
                            setCategoryIdToSave(WEBHOOK.ID);
                        }}
                        handleToggleIntegration={(integrationIdToToggle) => {
                            const webhook = { ...allWebhooks[integrationIdToToggle] };
                            webhook.enabled = !webhook.enabled;

                            dispatch({
                                type: actions.UPDATE_WEBHOOK,
                                payload: { id: webhook.id, value: webhook }
                            });
                            setCategoryIdToSave(WEBHOOK.ID);
                        }}
                    />
                )
            });
        }

        if (Object.keys(filteredSyslogServers).length) {
            rows.push({
                providerName: SYSLOG_SERVER.NAME,
                providerType: 'notifications',
                icon: Icons.Cube,
                totalInstances: Object.keys(allSyslogServers).length,
                extendedData: (
                    <AlertsManagementSubTable
                        isEditor={isEditor}
                        providerName={SYSLOG_SERVER.NAME}
                        integrations={Object.values(filteredSyslogServers)}
                        handleEditIntegration={(integrationIdToEdit) => {
                            setSyslogServerIdToEdit(integrationIdToEdit);
                        }}
                        handleDeleteIntegration={(integrationIdToRemove) => {
                            dispatch({ type: actions.REMOVE_SYSLOG_SERVER, payload: integrationIdToRemove });
                            setCategoryIdToSave(SYSLOG_SERVER.ID);
                        }}
                        handleToggleIntegration={(integrationIdToToggle) => {
                            const syslogServer = { ...allSyslogServers[integrationIdToToggle] };
                            syslogServer.enabled = !syslogServer.enabled;

                            dispatch({
                                type: actions.UPDATE_SYSLOG_SERVER,
                                payload: { id: syslogServer.id, value: syslogServer }
                            });
                            setCategoryIdToSave(SYSLOG_SERVER.ID);
                        }}
                    />
                )
            });
        }

        if (Object.keys(filteredEmails).length) {
            rows.push({
                providerName: EMAIL.NAME,
                providerType: 'notifications',
                icon: Icons.Mail,
                totalInstances: Object.keys(allEmails).length,
                extendedData: (
                    <AlertsManagementSubTable
                        isEditor={isEditor}
                        providerName={EMAIL.NAME}
                        integrations={Object.values(filteredEmails)}
                        handleEditIntegration={(integrationIdToEdit) => {
                            setEmailIdToEdit(integrationIdToEdit);
                        }}
                        handleDeleteIntegration={(integrationIdToRemove) => {
                            dispatch({ type: actions.REMOVE_EMAIL, payload: integrationIdToRemove });
                            setCategoryIdToSave(EMAIL.ID);
                        }}
                        handleToggleIntegration={(integrationIdToToggle) => {
                            const email = { ...allEmails[integrationIdToToggle] };
                            email.enabled = !email.enabled;
                            dispatch({
                                type: actions.UPDATE_EMAIL,
                                payload: { id: email.id, value: email }
                            });
                            setCategoryIdToSave(EMAIL.ID);
                        }}
                    />
                )
            });
        }

        if (Object.keys(filteredCustomIntegrations).length) {
            Object.entries(filteredCustomIntegrations).forEach(([key, integrations]) => {
                const providerSchema = customIntegrationsSchema.properties[key];

                rows.push({
                    providerName: providerSchema.name,
                    providerType: providerSchema.integrationType,
                    logo: providerSchema.logo,
                    totalInstances: integrations.length,
                    extendedData: (
                        <AlertsManagementSubTable
                            isEditor={isEditor}
                            providerName={providerSchema.name}
                            integrations={integrations}
                            keyExtractor={(integration) => integration.name}
                            lastModifiedKey="lastModifiedDate"
                            handleEditIntegration={(integrationIdToEdit) => {
                                setCustomIntegrationIdentifiersToEdit({
                                    providerKey: key,
                                    integrationId: integrationIdToEdit
                                });
                            }}
                            handleDeleteIntegration={async (integrationIdToRemove) => {
                                try {
                                    await httpDelete('/settings/removeIntegrationAlarm', {
                                        providerKey: key,
                                        configId: integrationIdToRemove
                                    });
                                } catch (error) {
                                    console.error('Failed to remove integration alarm:', error);
                                }

                                saveCustomIntegrations(
                                    key,
                                    integrations.filter(({ id }) => id !== integrationIdToRemove)
                                );
                            }}
                            handleToggleIntegration={(integrationIdToToggle) => {
                                saveCustomIntegrations(
                                    key,
                                    integrations.map((integration) =>
                                        integration.id === integrationIdToToggle
                                            ? { ...integration, enabled: !integration.enabled }
                                            : integration
                                    )
                                );
                            }}
                        />
                    )
                });
            });
        }

        return rows;
    }, [
        searchText,
        allEmails,
        allWebhooks,
        allSyslogServers,
        allCustomIntegrations,
        customIntegrationsSchema,
        isEditor,
        saveCustomIntegrations
    ]);

    const sortedRows = useMemo(() => {
        const rows = buildRows();
        const filteredRows = rows.filter((row) => {
            const { providerType } = row;
            const providerTypeFilter = selectedFilters['providerType'];
            return !providerTypeFilter.length || providerTypeFilter.includes(providerType);
        });

        return _.orderBy(filteredRows, [sortBy.columnKey, 'providerName'], [sortBy.direction, 'asc']);
    }, [buildRows, sortBy, selectedFilters]);

    if (!hasValues || isGalleryOpen) {
        return (
            <AlertsManagementGallery
                isEditor={isEditor}
                hasHttpProxy={hasHttpProxy}
                onClose={handleCloseGallery}
                webhooks={allWebhooks}
                syslogServers={allSyslogServers}
                emails={allEmails}
                customIntegrations={allCustomIntegrations}
                customIntegrationsSchema={customIntegrationsSchema}
                saveCustomIntegrations={saveCustomIntegrations}
                dispatch={dispatch}
                save={save}
                visibleFields={visibleFields}
            />
        );
    }

    if (emailIdToEdit) {
        return (
            <IntegrationEditorWrapper>
                <EmailDetails
                    isEdit={true}
                    data={allEmails[emailIdToEdit]}
                    otherEmails={Object.values(allEmails).filter((email) => email.id !== emailIdToEdit)}
                    onSave={(email) => {
                        dispatch({
                            type: actions.UPDATE_EMAIL,
                            payload: { id: email.id, value: email }
                        });
                        setCategoryIdToSave(EMAIL.ID);
                        setEmailIdToEdit('');
                    }}
                    onCancel={() => setEmailIdToEdit('')}
                />
            </IntegrationEditorWrapper>
        );
    }

    if (webhookIdToEdit) {
        return (
            <IntegrationEditorWrapper>
                <WebhookDetails
                    isEdit={true}
                    data={allWebhooks[webhookIdToEdit]}
                    otherWebhooks={Object.values(allWebhooks).filter((webhook) => webhook.id !== webhookIdToEdit)}
                    onSave={(webhook) => {
                        dispatch({
                            type: actions.UPDATE_WEBHOOK,
                            payload: { id: webhook.id, value: webhook }
                        });
                        setCategoryIdToSave(WEBHOOK.ID);
                        setWebhookIdToEdit('');
                    }}
                    onCancel={() => setWebhookIdToEdit('')}
                />
            </IntegrationEditorWrapper>
        );
    }

    if (syslogServerIdToEdit) {
        return (
            <IntegrationEditorWrapper>
                <SyslogServerDetails
                    isEdit={true}
                    data={allSyslogServers[syslogServerIdToEdit]}
                    otherSyslogServers={Object.values(allSyslogServers).filter(
                        (syslogServer) => syslogServer.id !== syslogServerIdToEdit
                    )}
                    onSave={(syslogServer) => {
                        dispatch({
                            type: actions.UPDATE_SYSLOG_SERVER,
                            payload: { id: syslogServer.id, value: syslogServer }
                        });
                        setCategoryIdToSave(SYSLOG_SERVER.ID);
                        setSyslogServerIdToEdit('');
                    }}
                    onCancel={() => setSyslogServerIdToEdit('')}
                />
            </IntegrationEditorWrapper>
        );
    }

    if (customIntegrationIdentifiersToEdit) {
        const providerKey = customIntegrationIdentifiersToEdit.providerKey;
        const providerSchema = customIntegrationsSchema.properties[providerKey];

        const providerName = providerSchema.name;
        const logo = providerSchema.logo;
        const integrations = allCustomIntegrations[providerKey];
        const integrationIdToEdit = customIntegrationIdentifiersToEdit.integrationId;

        return (
            <IntegrationEditorWrapper>
                <CustomIntegrationDetails
                    customIntegrationsSchema={customIntegrationsSchema}
                    providerKey={providerKey}
                    data={integrations.find((integration) => integration.id === integrationIdToEdit)}
                    otherCustomIntegrations={integrations.filter(
                        (integration) => integration.id !== integrationIdToEdit
                    )}
                    providerName={providerName}
                    logo={logo}
                    onSave={(integrationValue) => {
                        const updatedIntegrations = integrations.map((integration) =>
                            integration.id === integrationIdToEdit ? integrationValue : integration
                        );
                        saveCustomIntegrations(providerKey, updatedIntegrations);
                        setCustomIntegrationIdentifiersToEdit(undefined);
                    }}
                    onCancel={() => setCustomIntegrationIdentifiersToEdit(undefined)}
                    hasHttpProxy={hasHttpProxy}
                />
            </IntegrationEditorWrapper>
        );
    }

    return (
        <NewCategory
            category={ALERTS_MANAGEMENT}
            fields={{
                alertsManagement: ALERTS_MANAGEMENT_HASH_URLS.grid
            }}
            setSelectedTab={setSelectedTab}
            selectedTab={selectedTab}>
            {
                <>
                    <div className="mb-4">
                        <AlertsManagementFilters
                            searchText={searchText}
                            handleAddIntegration={handleAddIntegration}
                            onSearchChange={(text) => setSearchText(text)}
                            selectedFilters={selectedFilters}
                            setSelectedFilters={setSelectedFilters}
                        />
                    </div>
                    <Table
                        columns={[
                            {
                                columnKey: 'providerName',
                                title: 'Providers',
                                columnClassName: 'max-w-[380px]',
                                render: ({ providerName, icon, logo }) => (
                                    <div className="flex items-center gap-2">
                                        <div className="flex items-center justify-center">
                                            {logo !== undefined ? (
                                                <img alt="integration logog" className="w-5 h-5" src={logo} />
                                            ) : (
                                                <Icon className="text-sf-gray-400" icon={icon} size={20} />
                                            )}
                                        </div>
                                        <div>{providerName}</div>
                                    </div>
                                )
                            },
                            {
                                columnKey: 'totalInstances',
                                title: 'Integrations',
                                columnClassName: 'max-w-[128px]'
                            }
                        ]}
                        defaultSortColumn="providerName"
                        onSortChange={(columnKey, direction) => setSortBy({ columnKey, direction })}
                        expandableRowsMode={true}
                        expandedDataKey={'extendedData'}
                        keyExtractor={(item) => item.integrationName}
                        rows={sortedRows}
                        tableContainerClassName="w-[calc(100vw-360px)]"
                    />
                </>
            }
        </NewCategory>
    );
};

export default AlertsManagement;
